/**
 * SkyChart - Classe principale
 */
import { DEFAULT_OPTIONS } from './constants.js';
import { DataProcessor } from '../data/DataProcessor.js';
import { CircleRenderer } from '../renderers/CircleRenderer.js';
import { ZodiacRenderer } from '../renderers/ZodiacRenderer.js';
import { HouseRenderer } from '../renderers/HouseRenderer.js';
import { PlanetRenderer } from '../renderers/PlanetRenderer.js';
import { AspectRenderer } from '../renderers/AspectRenderer.js';

export class SkyChart {
    /**
     * @param {HTMLCanvasElement} canvas - Élément canvas à utiliser
     * @param {Object} astrologyChart - Données du thème astral
     */
    constructor(canvas, astrologyChart) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');

        // Fusionner les options par défaut avec celles fournies
        this.options = {
            ...DEFAULT_OPTIONS,
            size: Math.min(canvas.width, canvas.height)
        };

        // Initialiser les renderers
        this.circleRenderer = new CircleRenderer(this.ctx, this.options);
        this.zodiacRenderer = new ZodiacRenderer(this.ctx, this.options);
        this.houseRenderer = new HouseRenderer(this.ctx, this.options);
        this.planetRenderer = new PlanetRenderer(this.ctx, this.options);
        this.aspectRenderer = new AspectRenderer(this.ctx, this.options);

        // Préparer les données pour le rendu
        this.chartData = DataProcessor.prepareData(astrologyChart);
        // The wheel turns on the Ascendant (the cusp of house I): read by every renderer through the
        // options they share. Without houses, 0° Aries sits at nine o'clock.
        this.options.ascendant = this.chartData.houses?.[0]?.position ?? 0;

        // Premier rendu
        this._adjustCanvasSize();
        this.draw();

        // Gérer le redimensionnement
        this._setupResizeHandler();
    }

    /**
     * Redessine le thème astral
     */
    draw() {
        this._adjustCanvasSize();

        const ctx = this.ctx;
        const size = this.options.size;
        const center = size / 2;
        const radius = center - this.options.margin;
        // Canvas pas encore dimensionné (0px, ex. juste après insertion) → rayon négatif : on saute
        // ce rendu dégénéré, le ResizeObserver redessinera dès que le canvas a une taille.
        if (radius <= 0) return;

        // Effacer tout le canvas
        ctx.clearRect(0, 0, size, size);

        // Dessiner les cercles
        this.circleRenderer.draw(center, center, radius);

        // Dessiner les signes du zodiaque
        this.zodiacRenderer.drawZodiac(center, center, radius);

        // Dessiner les décans
        this.zodiacRenderer.drawDecans(center, center, radius);

        // Dessiner les cusps des maisons
        this.houseRenderer.draw(center, center, radius, this.chartData.houses);

        // Préparer un objet pour stocker les positions des planètes
        const planetPositions = {};

        // Calculer les positions des planètes sans les dessiner
        this.planetRenderer.calculatePositions(
            center, center, radius, this.chartData.planets, planetPositions
        );

        // Dessiner les aspects entre les planètes
        if (this.chartData.aspects && this.chartData.aspects.length) {
            // Filtrer pour ne garder que les aspects importants
            const importantAspects = DataProcessor.filterImportantAspects(this.chartData.aspects);

            // Trier les aspects par force pour dessiner d'abord les plus faibles
            const sortedAspects = [...importantAspects].sort((a, b) =>
                Math.abs(a.effectiveStrength) - Math.abs(b.effectiveStrength)
            );

            this.aspectRenderer.draw(sortedAspects, planetPositions);
        }

        // Dessiner les planètes en dernier (au-dessus des aspects)
        this.planetRenderer.draw(center, center, radius, this.chartData.planets, planetPositions);
    }

    /**
     * Gère le redimensionnement du canvas
     * @private
     */
    _adjustCanvasSize() {
        const rect = this.canvas.getBoundingClientRect();
        const pixelRatio = window.devicePixelRatio || 1;

        // Ajuster uniquement à la taille nécessaire (carré)
        const size = Math.min(rect.width, rect.height);
        this.canvas.width = size * pixelRatio;
        this.canvas.height = size * pixelRatio;

        // Ajuster le style pour correspondre à la taille visible
        this.canvas.style.width = `${size}px`;
        this.canvas.style.height = `${size}px`;

        this.ctx.scale(pixelRatio, pixelRatio);
        this.options.size = size;
    }

    /**
     * Configure l'observateur de redimensionnement
     * @private
     */
    _setupResizeHandler() {
        // Utiliser ResizeObserver si disponible
        if (window.ResizeObserver) {
            const observer = new ResizeObserver(() => {
                requestAnimationFrame(() => this.draw());
            });
            observer.observe(this.canvas);
        } else {
            // Fallback sur window.resize
            window.addEventListener('resize', () => {
                requestAnimationFrame(() => this.draw());
            });
        }
    }
}